import supabase from './supabase';
import { sendNotification } from './notificationService';

/**
 * Collaborator Service for routes
 * Requires DB objects:
 * - public.route_collaborators (route_id, user_id, role)
 * - RLS policies per migration
 * - user_profiles (user_id, username, avatar_url/profilePicture)
 */

/**
 * Search users by username/full_name/email (if available in user_profiles).
 * @param {string} query
 * @param {object} options
 * @param {number} [options.limit=10]
 * @returns {Promise<Array<{ user_id: string, username?: string, avatar_url?: string, profilePicture?: string, full_name?: string }>>}
 */
export async function searchUsers(query, { limit = 10 } = {}) {
  if (!query || !query.trim()) return [];

  // Use only columns that exist in this project's user_profiles
  const orFilters = [
    `username.ilike.%${query}%`,
    `name.ilike.%${query}%`,
    `email.ilike.%${query}%`
  ].join(',');

  const { data, error } = await supabase
    .from('user_profiles')
    .select('user_id, username, name, profilePicture')
    .or(orFilters)
    .limit(limit);

  if (error) throw error;
  return data || [];
}

/**
 * Add (or update) a collaborator role for a route. Owner-only via RLS.
 * @param {string} routeId
 * @param {string} userId
 * @param {'editor'|'viewer'} role
 */
export async function addCollaborator(routeId, userId, role = 'viewer') {
  if (!routeId || !userId) throw new Error('routeId and userId are required');
  if (!['editor', 'viewer'].includes(role)) throw new Error('Invalid role');

  const { data, error } = await supabase
    .from('route_collaborators')
    .upsert({ route_id: routeId, user_id: userId, role }, { onConflict: 'route_id,user_id' })
    .select('*')
    .single();

  if (error) throw error;
  // Send invite notification to the collaborator
  try {
    const { data: { user: actor } } = await supabase.auth.getUser();
    if (actor?.id) {
      await sendNotification({
        recipientId: userId,
        actorId: actor.id,
        type: 'route_invite',
        routeId: routeId,
        payload: { role }
      });
    }
  } catch (_) {}
  return data;
}

/**
 * List collaborators for a route, enriched with profile fields.
 * @param {string} routeId
 */
export async function listCollaborators(routeId) {
  if (!routeId) throw new Error('routeId is required');

  // Fetch collaborators
  const { data, error } = await supabase
    .from('route_collaborators')
    .select('user_id, role')
    .eq('route_id', routeId);
  if (error) throw error;

  const rows = data || [];
  if (rows.length === 0) return [];

  const userIds = rows.map(r => r.user_id);
  const { data: profiles, error: pErr } = await supabase
    .from('user_profiles')
    .select('user_id, username, name, profilePicture')
    .in('user_id', userIds);
  if (pErr) throw pErr;

  const byId = new Map((profiles || []).map(p => [p.user_id, p]));
  return rows.map(r => ({
    user_id: r.user_id,
    role: r.role,
    username: byId.get(r.user_id)?.username,
    name: byId.get(r.user_id)?.name,
    profilePicture: byId.get(r.user_id)?.profilePicture
  }));
}

/**
 * Remove a collaborator. Owner-only via RLS.
 * @param {string} routeId
 * @param {string} userId
 */
export async function removeCollaborator(routeId, userId) {
  if (!routeId || !userId) throw new Error('routeId and userId are required');
  const { error } = await supabase
    .from('route_collaborators')
    .delete()
    .match({ route_id: routeId, user_id: userId });
  if (error) throw error;
  return { success: true };
}

/**
 * Update collaborator role. Owner-only via RLS.
 * @param {string} routeId
 * @param {string} userId
 * @param {'editor'|'viewer'} role
 */
export async function updateCollaboratorRole(routeId, userId, role) {
  if (!routeId || !userId) throw new Error('routeId and userId are required');
  if (!['editor', 'viewer'].includes(role)) throw new Error('Invalid role');
  const { data, error } = await supabase
    .from('route_collaborators')
    .update({ role })
    .match({ route_id: routeId, user_id: userId })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/**
 * Permission helpers (wrappers of RPCs)
 */
export async function canViewRoute(routeId) {
  const { data, error } = await supabase.rpc('can_view_route', { p_route_id: routeId });
  if (error) throw error;
  return !!data;
}

export async function canEditRoute(routeId) {
  const { data, error } = await supabase.rpc('can_edit_route', { p_route_id: routeId });
  if (error) throw error;
  return !!data;
}

